import type { ConfigType } from "dayjs"
import type { DateString, DateTimeString } from "@/types/common"
import type { SafeAny } from "@/types/utils"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import duration from "dayjs/plugin/duration"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import relativeTime from "dayjs/plugin/relativeTime"
import timezone from "dayjs/plugin/timezone"
import _capitalize from "lodash/capitalize"
import isDateObject from "lodash/isDate"

import "dayjs/locale/it"
import "dayjs/locale/en"

const NUMERIC_TIMESTAMP_REGEX = /^\d{10,}$/

/*
import utc from "dayjs/plugin/utc"
dayjs.extend(utc)
dayjs.locale(locale_it)
dayjs.tz.setDefault(dayjs.tz.guess())
*/
dayjs.extend(duration)
dayjs.extend(isSameOrAfter)
dayjs.extend(customParseFormat)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

export function dFormat(val: ConfigType, type: "date" | "datetime" | "time" | "date_long"): string {
	let format = ""
	let parsedDate = val
	if (val && typeof parsedDate === "number" && parsedDate.toString().length >= 15) {
		parsedDate = parsedDate / 1000
	}
	const date = dayjs(parsedDate)
	if (!date.isValid()) return val?.toString() || ""

	switch (type) {
		case "date":
			format = "DD MMM YYYY"
			break
		case "datetime":
			format = "DD MMM YYYY, HH:mm"
			break
		case "time":
			format = "HH:mm"
			break
		case "date_long":
			format = "DD MMMM YYYY"
			break
	}

	return date
		.format(format)
		.split(" ")
		.map(o => _capitalize(o))
		.join(" ")
}

export function dPayload(val: ConfigType, type: "date"): DateString
export function dPayload(val: ConfigType, type: "datetime"): DateTimeString
export function dPayload(val: ConfigType, type: "date" | "datetime"): DateString | DateTimeString {
	let format = ""
	let parsedDate = val
	if (val && typeof parsedDate === "number" && parsedDate.toString().length >= 15) {
		parsedDate = parsedDate / 1000
	}
	const date = dayjs(parsedDate)
	if (!date.isValid()) {
		return "0000-00-00" as DateString
	}

	switch (type) {
		case "date":
			format = "YYYY-MM-DD"
			break
		case "datetime":
			format = "YYYY-MM-DD HH:mm:ss"
			break
	}

	return date.format(format) as DateString | DateTimeString
}

export function isDate(val?: SafeAny): boolean {
	if (val === undefined || val === null || val === "") return false

	if (isDateObject(val)) return true

	const strVal = String(val)

	// Check for numeric timestamps (seconds, ms or µs)
	// We enforce a minimum length of 10 digits to avoid false positives like "188" or "2"
	if (NUMERIC_TIMESTAMP_REGEX.test(strVal)) {
		const num = Number.parseInt(strVal)
		// Handle ms (13 digits) or µs (16+ digits) by normalizing to ms
		const date = strVal.length >= 13 ? dayjs(num / 10 ** (strVal.length - 13)) : dayjs(num * 1000)
		return date.isValid()
	}

	// For ISO strings and other complex formats, we use a hybrid approach
	// 1. Check for ISO-like strings (containing T and possibly Z or +/- offset)
	if (strVal.includes("T")) {
		// dayjs() parser is quite robust for ISO 8601 even without explicit format
		return dayjs(strVal).isValid()
	}

	// 2. Strict parsing for regional formats
	const regionalFormats = ["DD/MM/YYYY", "MM/DD/YYYY", "DD-MM-YYYY", "MM-DD-YYYY", "YYYY-MM-DD", "YYYY-DD-MM"]

	return dayjs(strVal, regionalFormats, true).isValid()
}

export default dayjs
